import { Navigate, useNavigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { ONBOARDING_STEP } from "./constants";
import { ONBOARDING_PANE, SIMPLE_ONBOARDING_PATHS } from "./routes";
import { CompanyStepScreen } from "./simple-onboarding/company-step-screen";
import { DocumentStepScreen } from "./simple-onboarding/document-step-screen";
import { PayrollReadyRedirect } from "./simple-onboarding/payroll-ready-redirect";
import { ScheduleStepScreen } from "./simple-onboarding/schedule-step-screen";
import { SubmittedScreen } from "./simple-onboarding/submitted-screen";
import { ONBOARDING_DOCUMENT_TYPE } from "./simple-onboarding/types";
import { WelcomeScreen } from "./simple-onboarding/welcome-screen";

const BUSINESS_REFRESH_INTERVAL_MS = 5000;

const OnboardingContent = () => {
  const navigate = useNavigate();
  const { pane } = useParams<{ pane: string }>();
  const { businessId } = useBusiness();
  const { data: business, isLoading } = useGetBusiness(businessId, undefined, {
    refetchInterval: BUSINESS_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });

  if (!business || isLoading) {
    return (
      <Stack
        fullWidth
        className="h-[calc(100vh-67px)]"
        align="center"
        justify="center"
      >
        <LoadingSpinner />
      </Stack>
    );
  }

  // Live: they are a customer now, so onboarding hands them straight to their payroll
  // rather than to a page about it. The redirect announces itself on the way through.
  if (business.onboarding_step === ONBOARDING_STEP.completed) {
    return <PayrollReadyRedirect />;
  }

  if (pane === ONBOARDING_PANE.welcome) {
    return (
      <WelcomeScreen
        businessName={business.name}
        onContinue={() => navigate(SIMPLE_ONBOARDING_PATHS.company)}
      />
    );
  }

  if (pane === ONBOARDING_PANE.company) {
    return (
      <CompanyStepScreen
        business={business}
        activePane={pane}
        onContinue={() => navigate(SIMPLE_ONBOARDING_PATHS.schedule)}
        onBack={() => navigate(SIMPLE_ONBOARDING_PATHS.welcome)}
      />
    );
  }

  if (pane === ONBOARDING_PANE.schedule) {
    return (
      <ScheduleStepScreen
        business={business}
        activePane={pane}
        onContinue={() => navigate(SIMPLE_ONBOARDING_PATHS.referenceData)}
        onBack={() => navigate(SIMPLE_ONBOARDING_PATHS.company)}
      />
    );
  }

  // Two steps rather than one, so each kind of document gets a page that can say what it
  // is asking for in full. Reference data first: a customer has last period's numbers to
  // hand long before they have the rules written down. Neither page holds anyone, so
  // Continue is always available.
  if (pane === ONBOARDING_PANE.referenceData) {
    return (
      <DocumentStepScreen
        businessId={business.id}
        type={ONBOARDING_DOCUMENT_TYPE.referenceData}
        activePane={pane}
        onBack={() => navigate(SIMPLE_ONBOARDING_PATHS.schedule)}
        onContinue={() => navigate(SIMPLE_ONBOARDING_PATHS.payPlan)}
      />
    );
  }

  // The end of the flow gets its own page: a finished state does not belong as a card on a
  // working page. Anyone who comes back while we build their payroll lands here.
  if (pane === ONBOARDING_PANE.submitted) {
    if (!business.documents_ready_at) {
      return <Navigate to={SIMPLE_ONBOARDING_PATHS.referenceData} replace />;
    }

    return (
      <SubmittedScreen
        businessId={business.id}
        businessName={business.name}
        readyAt={business.documents_ready_at}
        onReopened={() => navigate(SIMPLE_ONBOARDING_PATHS.referenceData)}
      />
    );
  }

  // Last, so the question of whether they are finished is asked on the page that holds the
  // document finishing requires.
  if (pane === ONBOARDING_PANE.payPlan) {
    return (
      <DocumentStepScreen
        businessId={business.id}
        type={ONBOARDING_DOCUMENT_TYPE.payPlan}
        documentsReadyAt={business.documents_ready_at}
        activePane={pane}
        onBack={() => navigate(SIMPLE_ONBOARDING_PATHS.referenceData)}
        onSubmitted={() => navigate(SIMPLE_ONBOARDING_PATHS.submitted)}
      />
    );
  }

  // Includes the retired /onboarding/setup and /onboarding/documents, so an old link still
  // lands on the first step.
  return <Navigate to={SIMPLE_ONBOARDING_PATHS.welcome} replace />;
};

const Onboarding = () => <OnboardingContent />;

export default Onboarding;
